import { z } from 'zod';

/**
 * A platform user. Maps 1:1 to Supabase auth.users.
 * One user can belong to multiple Accounts (workspaces) but has a single
 * primary account for billing & defaults.
 */
export const UserSchema = z.object({
  id: z.string().guid(),
  email: z.string().email(),
  fullName: z.string().nullable(),
  avatarUrl: z.string().url().nullable(),
  primaryAccountId: z.string().guid().nullable(),
  createdAt: z.string().datetime(),
  lastSeenAt: z.string().datetime().nullable(),
});

export type User = z.infer<typeof UserSchema>;

/**
 * First name from a full name — what AI drafts are signed with (API) and what
 * onboarding shows as the default (web). Directory-style "Last, First"
 * ("Callado, Lucas") gives the word after the comma, but only when a single
 * word precedes it, so "Jane Smith, CPA" stays Jane. Trailing punctuation is
 * dropped. Blank → null.
 * ponytail: a multi-word surname ("De La Cruz, Maria") falls back to the first
 * word; the workspace drafter_signoff_name is the escape hatch.
 */
export function firstNameOf(fullName: string | null | undefined): string | null {
  const name = fullName?.trim() ?? '';
  const lastFirst = name.match(/^[^\s,]+,\s*([^\s,]+)/);
  const first = lastFirst ? lastFirst[1] : name.split(/\s+/)[0];
  return first?.replace(/\p{P}+$/u, '') || null;
}

/**
 * Role within an Account (workspace).
 */
export const AccountRoleSchema = z.enum(['owner', 'admin', 'member', 'viewer']);
export type AccountRole = z.infer<typeof AccountRoleSchema>;

/**
 * Join table — which users belong to which accounts and with what role.
 */
export const AccountMembershipSchema = z.object({
  userId: z.string().guid(),
  accountId: z.string().guid(),
  role: AccountRoleSchema,
  invitedBy: z.string().guid().nullable(),
  joinedAt: z.string().datetime(),
});

export type AccountMembership = z.infer<typeof AccountMembershipSchema>;
